import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { showApiError } from '../../utils/errorUtils';
import EntityLink from '../../components/Common/EntityLink';
import { useAuth } from '../../contexts/AuthContext';
import { hasAnyRole } from '../../utils/roleUtils';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import PaymentDialog from './PaymentDialog';
import './Invoices.css';
import { fetchAllCustomers, fetchAllProducts } from '../../utils/fetchAllPages';

// Invoices that still accept payments, and the roles that may record them (#307)
const PAYABLE_STATUSES = ['SENT', 'VIEWED', 'PARTIAL', 'OVERDUE'];
const VOIDABLE_STATUSES = ['SENT', 'VIEWED', 'OVERDUE'];
export const PAYMENT_ROLES = ['ADMIN', 'MANAGER', 'COSTING'];

const ViewInvoice = forwardRef(({ invoice, mode, onSave, onCancel, onDelete, onBack, onEdit }, ref) => {
  const { t } = useTranslation('sales');
  const { t: tCommon } = useTranslation('common');
  const { showError, showSuccess } = useMessages();
  const { user } = useAuth();
  const [formData, setFormData] = useState(getDefaultInvoice());
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleSave: handleSaveClick,
    handleDelete: handleDeleteClick,
    handleStatusChange,
    formData,
    isLoading
  }));

  const loadInvoiceData = useCallback(async (invoiceId) => {
    setLoadingInvoice(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/invoices/${invoiceId}`));

      if (response.ok) {
        const invoiceData = await response.json();
        setFormData({
          ...invoiceData,
          invoice_date: invoiceData.invoice_date ? formatDateForInput(invoiceData.invoice_date) : '',
          due_date: invoiceData.due_date ? formatDateForInput(invoiceData.due_date) : '',
          lines: invoiceData.lines || []
        });
        setErrors({});
      } else {
        console.error('Failed to load invoice:', invoiceId);
        setErrors({ general: t('invoices.view.error.fetchFailed') });
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      setErrors({ general: t('invoices.view.error.loadingError') });
    } finally {
      setLoadingInvoice(false);
    }
  }, []);

  // Load invoice data when invoice prop changes
  useEffect(() => {
    if (invoice && invoice.invoice_id && (mode === 'view' || mode === 'edit')) {
      loadInvoiceData(invoice.invoice_id);
    } else if (mode === 'create') {
      setFormData(getDefaultInvoice());
    }
  }, [invoice, mode, loadInvoiceData]);

  // Load reference data
  useEffect(() => {
    if (isCreateMode || isEditMode) {
      loadCustomers();
      loadProducts();
    }
  }, [isCreateMode, isEditMode]);

  // Helper function to find the best matching product for an invoice line
  const findMatchingProduct = useCallback((line) => {
    if (!products.length) return null;

    // Strategy 1: Direct product_id match
    if (line.product_id) {
      const directMatch = products.find(p => p.product_id === line.product_id);
      if (directMatch) return directMatch;
    }

    // Strategy 2: Product code match
    if (line.product_code) {
      const codeMatch = products.find(p => p.product_code === line.product_code);
      if (codeMatch) return codeMatch;
    }

    // Strategy 3: Product name match
    if (line.product_name) {
      const nameMatch = products.find(p => p.product_name === line.product_name);
      if (nameMatch) return nameMatch;
    }

    return null;
  }, [products]);

  // Auto-match products when products are loaded and lines exist
  useEffect(() => {
    if (products.length > 0 && formData.lines.length > 0 && (isEditMode || isCreateMode)) {
      let hasUpdates = false;
      const updatedLines = formData.lines.map(line => {
        // Only auto-match if there's no product_id but there is product data
        if (!line.product_id && (line.product_code || line.product_name)) {
          const matchedProduct = findMatchingProduct(line);
          if (matchedProduct) {
            hasUpdates = true;
            return {
              ...line,
              product_id: matchedProduct.product_id
            };
          }
        }
        return line;
      });

      if (hasUpdates) {
        setFormData(prev => ({
          ...prev,
          lines: updatedLines
        }));
      }
    }
  }, [products, formData.lines, isEditMode, isCreateMode, findMatchingProduct]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.tagName === 'SELECT' ||
          e.target.contentEditable === 'true') {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        // Different behavior based on mode
        if (isEditMode || isCreateMode) {
          // From edit/create mode, go back to view mode (or cancel if onCancel exists)
          if (onCancel) {
            onCancel();
          } else if (onBack) {
            onBack();
          }
        } else if (isViewMode && onBack) {
          // From view mode, go back to list
          onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isEditMode, isCreateMode, isViewMode, onBack, onCancel]);

  // Recalculate totals when shipping or handling costs change
  useEffect(() => {
    if (formData.lines.length > 0) {
      calculateTotals(formData);
    }
  }, [formData.shipping_cost, formData.handling_fee]);

  // Default invoice structure
  function getDefaultInvoice() {
    const today = localToday();

    return {
      sales_order_id: null,
      customer_id: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      invoice_date: today,
      due_date: localToday(30),
      payment_terms: 'Net 30',
      po_number: '',
      billing_address: '',
      shipping_address: '',
      notes: '',
      internal_notes: '',
      currency_code: 'USD',
      status: 'DRAFT',
      payment_status: 'UNPAID',
      subtotal_amount: 0,
      tax_amount: 0,
      discount_amount: 0,
      shipping_method: '',
      shipping_cost: 0,
      handling_fee: 0,
      total_amount: 0,
      amount_paid: 0,
      amount_due: 0,
      lines: []
    };
  }

  // API functions
  const loadCustomers = async () => {
    try {
      setCustomers(await fetchAllCustomers({ is_active: 'true' })); // active ones, all pages (#310, #384)
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchAllProducts());
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  // Utility functions
  function formatDateForInput(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }

  function statusLabel(status) {
    return status ? t(`invoices.view.status.${status.toLowerCase()}`, { defaultValue: status }) : '';
  }

  function paymentStatusLabel(status) {
    return status ? t(`invoices.view.paymentStatus.${status.toLowerCase()}`, { defaultValue: status }) : '';
  }

  // Event handlers
  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.customer_id === parseInt(customerId));
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: parseInt(customerId),
        customer_name: customer.customer_name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        billing_address: customer.billing_address || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        billing_address: ''
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleLineChange = (index, field, value) => {
    const updatedLines = [...formData.lines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value
    };

    // Recalculate line amounts
    if (['quantity_invoiced', 'unit_price', 'discount_percentage', 'discount_amount', 'tax_rate'].includes(field)) {
      updatedLines[index] = calculateLineAmounts(updatedLines[index]);
    }

    setFormData(prev => ({
      ...prev,
      lines: updatedLines
    }));

    // Recalculate totals
    calculateTotals({ ...formData, lines: updatedLines });
  };

  const calculateLineAmounts = (line) => {
    const quantity = parseFloat(line.quantity_invoiced || 0);
    const unitPrice = parseFloat(line.unit_price || 0);
    const discountPercentage = parseFloat(line.discount_percentage || 0);
    const discountAmount = parseFloat(line.discount_amount || 0);
    const taxRate = parseFloat(line.tax_rate || 0);

    const grossAmount = quantity * unitPrice;
    const discountFromPercentage = grossAmount * (discountPercentage / 100);
    const totalDiscount = discountFromPercentage + discountAmount;
    const netAmount = grossAmount - totalDiscount;
    const taxAmount = netAmount * (taxRate / 100);
    const lineTotal = netAmount + taxAmount;

    return {
      ...line,
      line_amount: netAmount.toFixed(4),
      tax_amount: taxAmount.toFixed(4),
      line_total: lineTotal.toFixed(4)
    };
  };

  const calculateTotals = (invoiceData) => {
    const lines = invoiceData.lines || [];

    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;

    lines.forEach(line => {
      const quantity = parseFloat(line.quantity_invoiced || 0);
      const unitPrice = parseFloat(line.unit_price || 0);
      const discountAmount = parseFloat(line.discount_amount || 0);
      const discountPercentage = parseFloat(line.discount_percentage || 0);
      const taxAmount = parseFloat(line.tax_amount || 0);

      const grossAmount = quantity * unitPrice;
      const lineDiscount = discountAmount + (grossAmount * discountPercentage / 100);

      subtotal += grossAmount;
      totalDiscount += lineDiscount;
      totalTax += taxAmount;
    });

    const shippingCost = parseFloat(invoiceData.shipping_cost) || 0;
    const handlingFee = parseFloat(invoiceData.handling_fee) || 0;
    const total = subtotal - totalDiscount + totalTax + shippingCost + handlingFee;
    const amountDue = total - (parseFloat(invoiceData.amount_paid) || 0);

    const updatedData = {
      ...invoiceData,
      subtotal_amount: subtotal.toFixed(4),
      discount_amount: totalDiscount.toFixed(4),
      tax_amount: totalTax.toFixed(4),
      // Don't override shipping_cost and handling_fee to allow free editing
      total_amount: total.toFixed(4),
      amount_due: amountDue.toFixed(4)
    };

    setFormData(updatedData);
    return updatedData;
  };

  const addLine = () => {
    const newLine = {
      line_number: formData.lines.length + 1,
      sales_order_line_id: null,
      product_id: '',
      product_code: '',
      product_name: '',
      description: '',
      quantity_invoiced: 1,
      unit_price: 0,
      discount_percentage: 0,
      discount_amount: 0,
      tax_rate: 0,
      tax_amount: 0,
      line_amount: 0,
      line_total: 0
    };

    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, newLine]
    }));
  };

  const removeLine = (index) => {
    const updatedLines = formData.lines.filter((_, i) => i !== index);
    // Renumber lines
    updatedLines.forEach((line, i) => {
      line.line_number = i + 1;
    });

    const updatedFormData = {
      ...formData,
      lines: updatedLines
    };

    calculateTotals(updatedFormData);
  };

  const handleProductChange = (index, productId) => {
    const numericProductId = productId ? parseInt(productId, 10) : null;
    const product = products.find(p => p.product_id === numericProductId);

    // Get current line data
    const currentLine = formData.lines[index];

    // Create updated line in one go to avoid multiple state updates
    let updatedLine;
    if (product) {
      // Update with current product data from products table
      updatedLine = {
        ...currentLine,
        product_id: numericProductId,
        product_code: product.product_code,
        product_name: product.product_name,
        description: product.description || '',
        // Only update price if it's currently 0 or this is a new line, preserve existing custom pricing
        unit_price: (!currentLine.unit_price || currentLine.unit_price === 0) ? (product.standard_price || 0) : currentLine.unit_price
      };
    } else {
      // Handle clearing product selection
      updatedLine = {
        ...currentLine,
        product_id: null,
        product_code: '',
        product_name: '',
        unit_price: 0,
        description: ''
      };
    }

    // Recalculate line amounts for the updated line
    const lineWithAmounts = calculateLineAmounts(updatedLine);

    // Update the lines array with the new line data
    const updatedLines = [...formData.lines];
    updatedLines[index] = lineWithAmounts;

    // Update form data and recalculate totals in one go
    const updatedFormData = { ...formData, lines: updatedLines };
    calculateTotals(updatedFormData);
  };


  const applyInvoice = (updatedInvoice) => {
    setFormData({
      ...updatedInvoice,
      invoice_date: updatedInvoice.invoice_date ? formatDateForInput(updatedInvoice.invoice_date) : '',
      due_date: updatedInvoice.due_date ? formatDateForInput(updatedInvoice.due_date) : '',
      lines: updatedInvoice.lines || [],
      payments: updatedInvoice.payments || []
    });
  };

  // Called by PaymentDialog; throws on error so the dialog stays open
  const handleRecordPayment = async (paymentData) => {
    const response = await apiFetch(getApiUrl(`/api/invoices/${formData.invoice_id}/payments`), {
      method: 'POST',
      body: JSON.stringify({
        payment_amount: paymentData.payment_amount,
        payment_method: paymentData.payment_method,
        payment_reference: paymentData.payment_reference || null,
        payment_date: paymentData.payment_date || null,
        notes: paymentData.notes || null
      })
    });
    if (!response.ok) {
      await showApiError(response, showError, tCommon);
      throw new Error('payment failed');
    }
    applyInvoice(await response.json());
    showSuccess(t('invoices.view.messages.paymentRecordedSuccess'),
      t('invoices.view.messages.paymentRecorded', { amount: formatCurrency(paymentData.payment_amount) }));
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'VOID' &&
        !window.confirm(t('invoices.view.messages.voidConfirmation', { invoiceNumber: formData.invoice_number }))) {
      return;
    }
    try {
      const response = await apiFetch(getApiUrl(`/api/invoices/${formData.invoice_id}/status`), {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        await showApiError(response, showError, tCommon);
        return;
      }

      applyInvoice(await response.json());

      alert(t('invoices.view.messages.statusUpdated', { status: statusLabel(newStatus) }));
    } catch (error) {
      console.error('Error updating status:', error);
      showError(t('invoices.view.messages.statusUpdateFailed'), error.message);
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = t('invoices.view.validation.customerRequired');
    }

    if (!formData.invoice_date) {
      newErrors.invoice_date = t('invoices.view.validation.invoiceDateRequired');
    }

    if (formData.lines.length === 0) {
      newErrors.lines = t('invoices.view.validation.linesRequired');
    }

    formData.lines.forEach((line, index) => {
      if (!line.quantity_invoiced || line.quantity_invoiced <= 0) {
        newErrors[`line_${index}_quantity`] = t('invoices.view.validation.quantityRequired');
      }
      if (!line.unit_price || line.unit_price < 0) {
        newErrors[`line_${index}_price`] = t('invoices.view.validation.unitPriceRequired');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Prepare data for API
      const saveData = {
        sales_order_id: formData.sales_order_id || null,
        customer_id: parseInt(formData.customer_id),
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        payment_terms: formData.payment_terms,
        po_number: formData.po_number,
        billing_address: formData.billing_address,
        shipping_address: formData.shipping_address,
        notes: formData.notes,
        internal_notes: formData.internal_notes,
        currency_code: formData.currency_code,
        status: formData.status,
        shipping_method: formData.shipping_method,
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        handling_fee: parseFloat(formData.handling_fee) || 0,
        lines: formData.lines.map(line => ({
          sales_order_line_id: line.sales_order_line_id || null,
          product_id: line.product_id || null,
          product_code: line.product_code,
          product_name: line.product_name,
          description: line.description,
          quantity_invoiced: parseFloat(line.quantity_invoiced),
          unit_price: parseFloat(line.unit_price),
          discount_percentage: parseFloat(line.discount_percentage || 0),
          discount_amount: parseFloat(line.discount_amount || 0),
          tax_rate: parseFloat(line.tax_rate || 0)
        }))
      };

      const url = isCreateMode ? '/api/invoices/' : `/api/invoices/${formData.invoice_id}`;
      const method = isCreateMode ? 'POST' : 'PUT';

      const response = await apiFetch(getApiUrl(url), {
        method,
        body: JSON.stringify(saveData)
      });

      if (!response.ok) {
        await showApiError(response, showError, tCommon);
        return;
      }

      const savedInvoice = await response.json();

      if (onSave) {
        onSave(savedInvoice);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      showError(t('invoices.view.messages.saveFailed'), error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Methods exposed to parent via imperative handle
  const handleSaveClick = async () => {
    await handleSave();
  };

  const handleDeleteClick = async () => {
    if (!formData.invoice_id) {
      return;
    }

    if (!window.confirm(t('invoices.view.messages.deleteConfirmation', { invoiceNumber: formData.invoice_number }))) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiFetch(getApiUrl(`/api/invoices/${formData.invoice_id}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        await showApiError(response, showError, tCommon);
        return;
      }

      alert(t('invoices.view.messages.invoiceDeleted'));

      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showError(t('invoices.view.messages.deleteFailed'), error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Status badge rendering
  const renderStatusBadge = (status, type = 'status') => {
    const getStatusClass = (status, type) => {
      if (type === 'payment_status') {
        switch (status) {
          case 'PAID': return 'status-success';
          case 'PARTIAL': return 'status-warning';
          case 'UNPAID': return 'status-error';
          case 'REFUNDED': return 'status-inactive';
          default: return 'status-inactive';
        }
      } else {
        switch (status) {
          case 'DRAFT': return 'status-warning';
          case 'SENT': return 'status-info';
          case 'VIEWED': return 'status-info';
          case 'PAID': return 'status-success';
          case 'PARTIAL': return 'status-warning';
          case 'OVERDUE': return 'status-error';
          case 'CANCELLED': return 'status-inactive';
          case 'VOID': return 'status-inactive';
          default: return 'status-inactive';
        }
      }
    };

    return (
      <span className={`status-badge ${getStatusClass(status, type)}`}>
        {type === 'payment_status' ? paymentStatusLabel(status) : statusLabel(status)}
      </span>
    );
  };

  const renderPayments = () => (
    <div className="invoice-payments">
      <h3>{t('invoices.view.payments.title')}</h3>
      {(formData.payments || []).length === 0 ? (
        <div className="no-data">{t('invoices.view.payments.none')}</div>
      ) : (
        <div className="lines-table">
          <table>
            <thead>
              <tr>
                <th>{t('invoices.view.payments.date')}</th>
                <th>{t('invoices.view.payments.amount')}</th>
                <th>{t('invoices.view.payments.method')}</th>
                <th>{t('invoices.view.payments.reference')}</th>
                <th>{t('invoices.view.payments.notes')}</th>
                <th>{t('invoices.view.payments.recordedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {formData.payments.map(payment => (
                <tr key={payment.payment_id}>
                  <td>{formatAppDate(payment.payment_date)}</td>
                  <td className="amount">{formatCurrency(payment.payment_amount)}</td>
                  <td>{payment.payment_method || '—'}</td>
                  <td>{payment.payment_reference || '—'}</td>
                  <td>{payment.notes || '—'}</td>
                  <td>{payment.created_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );


  const renderLines = () => (
    <div className="invoice-lines">
      <div className="lines-header">
        <h3>{t('invoices.view.sections.lineItems')}</h3>
        {!isViewMode && (
          <button onClick={addLine} className="btn-secondary">
            + {t('invoices.view.buttons.addLine')}
          </button>
        )}
      </div>

      {errors.lines && <div className="error-text">{errors.lines}</div>}

      <div className="lines-table">
        <table>
          <thead>
            <tr>
              <th>{t('invoices.view.fields.product')}</th>
              <th>{t('invoices.view.fields.description')}</th>
              <th>{t('invoices.view.fields.quantity')}</th>
              <th>{t('invoices.view.fields.unitPrice')}</th>
              <th>{t('invoices.view.fields.discount')} %</th>
              <th>{t('invoices.view.fields.discount')} $</th>
              <th>{t('invoices.view.fields.tax')} %</th>
              <th>{t('invoices.view.fields.total')}</th>
              {!isViewMode && <th>{t('common.actions.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {formData.lines.length === 0 ? (
              <tr>
                <td colSpan={isViewMode ? 8 : 9} className="no-data">
                  {t('invoices.view.noData.noLineItems')}
                </td>
              </tr>
            ) : (
              formData.lines.map((line, index) => (
                <tr key={index}>
                  <td>
                    {isViewMode ? (
                      <div>
                        {line.product_code && <strong>{line.product_code}</strong>}
                        <div>{line.product_name}</div>
                      </div>
                    ) : (
                      <div className="product-select-container">
                        <select
                          value={line.product_id ? line.product_id.toString() : ''}
                          onChange={(e) => handleProductChange(index, e.target.value)}
                          className={(() => {
                            // Only show warning if no product is selected but there's leftover product data from original invoice
                            const hasNoSelection = !line.product_id;
                            const hasLegacyData = line.product_code || line.product_name;
                            return hasNoSelection && hasLegacyData ? 'no-match-warning' : '';
                          })()}
                        >
                          <option value="" disabled hidden>{t('invoices.view.placeholders.selectProduct')}</option>
                          {products.map(product => (
                            <option key={product.product_id} value={product.product_id.toString()}>
                              {product.product_code} - {product.product_name}
                            </option>
                          ))}
                        </select>
                        {(() => {
                          // Only show warning if no product is selected but there's leftover product data
                          const hasNoSelection = !line.product_id;
                          const hasLegacyData = line.product_code || line.product_name;
                          if (hasNoSelection && hasLegacyData) {
                            return (
                              <div className="product-warning" title={t('invoices.view.messages.originalProduct', { code: line.product_code, name: line.product_name })}>
                                ⚠️ {t('invoices.view.messages.productNotFound')}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      line.description
                    ) : (
                      <input
                        type="text"
                        value={line.description || ''}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                        placeholder={t('invoices.view.placeholders.enterDescription')}
                      />
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      line.quantity_invoiced
                    ) : (
                      <input
                        type="number"
                        value={line.quantity_invoiced}
                        onChange={(e) => handleLineChange(index, 'quantity_invoiced', e.target.value)}
                        min="0"
                        step="0.001"
                        className={errors[`line_${index}_quantity`] ? 'error' : ''}
                      />
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      formatCurrency(line.unit_price)
                    ) : (
                      <input
                        type="number"
                        value={line.unit_price}
                        onChange={(e) => handleLineChange(index, 'unit_price', e.target.value)}
                        min="0"
                        step="0.01"
                        className={errors[`line_${index}_price`] ? 'error' : ''}
                      />
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      `${line.discount_percentage || 0}%`
                    ) : (
                      <input
                        type="number"
                        value={line.discount_percentage || 0}
                        onChange={(e) => handleLineChange(index, 'discount_percentage', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      formatCurrency(line.discount_amount)
                    ) : (
                      <input
                        type="number"
                        value={line.discount_amount || 0}
                        onChange={(e) => handleLineChange(index, 'discount_amount', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    )}
                  </td>
                  <td>
                    {isViewMode ? (
                      `${line.tax_rate || 0}%`
                    ) : (
                      <input
                        type="number"
                        value={line.tax_rate || 0}
                        onChange={(e) => handleLineChange(index, 'tax_rate', e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    )}
                  </td>
                  <td className="amount">
                    {formatCurrency(line.line_total)}
                  </td>
                  {!isViewMode && (
                    <td>
                      <button
                        onClick={() => removeLine(index)}
                        className="action-button delete"
                        title={t('invoices.view.buttons.removeLine')}
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTotals = () => (
    <div className="invoice-totals">
      <div className="totals-section">
        <div className="totals-row">
          <span className="label">{t('invoices.view.fields.subtotal')}:</span>
          <span className="amount">{formatCurrency(formData.subtotal_amount)}</span>
        </div>
        <div className="totals-row">
          <span className="label">{t('invoices.view.fields.discount')}:</span>
          <span className="amount">-{formatCurrency(formData.discount_amount)}</span>
        </div>
        <div className="totals-row">
          <span className="label">{t('invoices.view.fields.tax')}:</span>
          <span className="amount">{formatCurrency(formData.tax_amount)}</span>
        </div>
        {(formData.shipping_cost > 0 || formData.handling_fee > 0) && (
          <>
            <div className="totals-row">
              <span className="label">{t('invoices.view.fields.shipping')}:</span>
              <span className="amount">{formatCurrency(formData.shipping_cost)}</span>
            </div>
            {formData.handling_fee > 0 && (
              <div className="totals-row">
                <span className="label">{t('invoices.view.fields.handling')}:</span>
                <span className="amount">{formatCurrency(formData.handling_fee)}</span>
              </div>
            )}
          </>
        )}
        <div className="totals-row total-row">
          <span className="label">{t('invoices.view.fields.totalAmount')}:</span>
          <span className="amount">{formatCurrency(formData.total_amount)}</span>
        </div>
        {isViewMode && (
          <>
            <div className="totals-row">
              <span className="label">{t('invoices.view.fields.amountPaid')}:</span>
              <span className="amount">-{formatCurrency(formData.amount_paid)}</span>
            </div>
            <div className="totals-row due-row">
              <span className="label">{t('invoices.view.fields.amountDue')}:</span>
              <span className="amount">{formatCurrency(formData.amount_due)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderShipping = () => (
    <div className="invoice-shipping">
      <div className="shipping-section">
        <h3>{t('invoices.view.sections.shippingAndHandling')}</h3>
        <div className="shipping-fields">
          <div className="field-group">
            <label>{t('invoices.view.fields.shippingMethod')}</label>
            {isViewMode ? (
              <div className="field-value">{formData.shipping_method || t('common.shippingMethods.standard')}</div>
            ) : (
              <select
                value={formData.shipping_method || ''}
                onChange={(e) => handleInputChange('shipping_method', e.target.value)}
              >
                <option value="" disabled hidden>{t('invoices.view.placeholders.selectShippingMethod')}</option>
                <option value="STANDARD">{t('common.shippingMethods.standard')}</option>
                <option value="EXPRESS">{t('common.shippingMethods.express')}</option>
                <option value="OVERNIGHT">{t('common.shippingMethods.overnight')}</option>
                <option value="FREE">{t('common.shippingMethods.free')}</option>
              </select>
            )}
          </div>

          <div className="field-group">
            <label>{t('invoices.view.fields.shippingCost')}</label>
            {isViewMode ? (
              <div className="field-value">{formatCurrency(formData.shipping_cost)}</div>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                value={formData.shipping_cost ?? ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    shipping_cost: e.target.value
                  }));
                }}
                placeholder="0.00"
              />
            )}
          </div>

          <div className="field-group">
            <label>{t('invoices.view.fields.handlingFee')}</label>
            {isViewMode ? (
              <div className="field-value">{formatCurrency(formData.handling_fee)}</div>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                value={formData.handling_fee ?? ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    handling_fee: e.target.value
                  }));
                }}
                placeholder="0.00"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotes = () => (
    <div className="invoice-notes">
      <div className="notes-section">
        <div className="field-group">
          <label>{t('invoices.view.fields.notes')}</label>
          {isViewMode ? (
            <div className="field-value">{formData.notes || t('invoices.view.defaultValues.notAvailable')}</div>
          ) : (
            <textarea
              value={formData.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={t('invoices.view.placeholders.enterNotes')}
              rows="3"
            />
          )}
        </div>

        {!isViewMode && (
          <div className="field-group">
            <label>{t('invoices.view.fields.internalNotes')}</label>
            <textarea
              value={formData.internal_notes || ''}
              onChange={(e) => handleInputChange('internal_notes', e.target.value)}
              placeholder={t('invoices.view.placeholders.enterInternalNotes')}
              rows="3"
            />
          </div>
        )}
      </div>
    </div>
  );


  const canRecordPayment = isViewMode && PAYABLE_STATUSES.includes(formData.status) &&
    parseFloat(formData.amount_due) > 0 && hasAnyRole(user, PAYMENT_ROLES);

  // Main render
  if (loadingInvoice) {
    return (
      <div className="view-invoice">
        <div className="loading-container">
          <div className="loading">{t('invoices.view.loading')}</div>
        </div>
      </div>
    );
  }

  if (errors.general) {
    return (
      <div className="view-invoice">
        <div className="error-container">
          <div className="error-message">{errors.general}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {isCreateMode ? t('invoices.view.titles.newInvoice') :
             isEditMode ? t('invoices.view.titles.editInvoice', { invoiceNumber: formData.invoice_number }) :
             t('invoices.view.titles.invoice', { invoiceNumber: formData.invoice_number || t('invoices.view.status.draft') })}
          </h1>
          {!isCreateMode && (
            <div className="header-badges">
              {renderStatusBadge(formData.status)}
              {renderStatusBadge(formData.payment_status, 'payment_status')}
              {formData.sales_order_id && (
                <EntityLink
                  type="sales-order"
                  id={formData.sales_order_id}
                  label={formData.sales_order_number || `SO-${formData.sales_order_id}`}
                  returnLabel={formData.invoice_number}
                  returnType="invoice"
                  returnId={formData.invoice_id}
                />
              )}
            </div>
          )}
        </div>
        <div className="header-actions">
          {isViewMode ? (
            <>


              {formData.status === 'DRAFT' && (
                <>
                  <button
                    className="btn-primary"
                    onClick={() => handleStatusChange('SENT')}
                    disabled={!formData.invoice_id}
                  >
                    📤 {t('invoices.view.buttons.markAsSent')}
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleStatusChange('CANCELLED')}
                    disabled={!formData.invoice_id}
                  >
                    ❌ {t('invoices.view.buttons.cancel')}
                  </button>
                </>
              )}

              {canRecordPayment && (
                <button
                  className="btn-primary"
                  onClick={() => setShowPaymentDialog(true)}
                  disabled={!formData.invoice_id}
                >
                  💵 {t('invoices.view.buttons.recordPayment')}
                </button>
              )}

              {formData.status === 'SENT' && (
                <button
                  className="btn-danger"
                  onClick={() => handleStatusChange('CANCELLED')}
                  disabled={!formData.invoice_id}
                >
                  ❌ {t('invoices.view.buttons.cancel')}
                </button>
              )}

              {VOIDABLE_STATUSES.includes(formData.status) && !(parseFloat(formData.amount_paid) > 0) && (
                <button
                  className="btn-warning"
                  onClick={() => handleStatusChange('VOID')}
                  disabled={!formData.invoice_id}
                >
                  🚫 {t('invoices.view.buttons.void')}
                </button>
              )}


              <button className="btn-secondary" onClick={onEdit}>{t('invoices.view.buttons.edit')}</button>
            </>
          ) : (
            <>
              <button
                className="btn-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                {t('invoices.view.buttons.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (isCreateMode ? t('invoices.view.buttons.creating') : t('invoices.view.buttons.saving')) : (isCreateMode ? t('invoices.view.buttons.create') : t('invoices.view.buttons.save'))}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="invoice-content">
        {renderLines()}
        {renderShipping()}
        {renderTotals()}
        {isViewMode && renderPayments()}
        {renderNotes()}
      </div>

      {showPaymentDialog && (
        <PaymentDialog
          invoice={formData}
          isOpen={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
          onSave={handleRecordPayment}
        />
      )}

    </div>
  );
});

ViewInvoice.displayName = 'ViewInvoice';

export default ViewInvoice;